import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  Card,
  Divider,
  Icon,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  AlertTitle,
  CircularProgress,
} from "@mui/material";
import DataTableHeadCell from "../../examples/Tables/DataTable/DataTableHeadCell";
import DataTable from "../../examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useMaterialUIController } from "context";
import { fetchWithCsrfRetry } from "api/csrf";

// API imports
// Use direct fetch to backend endpoints

// Utils
import { getCurrentUserDepartment, getUserDisplayName } from "utils/userUtils";

function CustomerProfile() {
  const { idNama } = useParams();
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // State for data - simplified to single profileData object
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userDepartment, setUserDepartment] = useState("");
  const [navigating, setNavigating] = useState(false);
  
  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });

  useEffect(() => {
    // Set user department when component mounts
    setUserDepartment(getCurrentUserDepartment());
  }, []);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!idNama) {
          throw new Error("Customer ID is required");
        }

        // Single API call to get customer with all related akaun records
        const response = await fetch(`/api/nama/${idNama}`, {
          headers: {
            "X-User-Department": getCurrentUserDepartment(),
          },
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Customer not found");
          }
          throw new Error("Failed to load customer profile");
        }

        const data = await response.json();
        setProfileData(data.data);
      } catch (err) {
        console.error("Error fetching profile data:", err);
        setError(err.message || "Failed to load profile data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfileData();
  }, [idNama]);

  // Retry function for error handling
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    // Trigger useEffect by changing a dependency - we'll re-fetch based on idNama
    window.location.reload();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox mb={2} />
        
        {/* Header Skeleton */}
        <MDBox position="relative" mb={2}>
          <Skeleton variant="rectangular" height={96} sx={{ borderRadius: "xl" }} />
          <Card sx={{ position: "relative", mt: -8, mx: 3, py: 2, px: 2 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item>
                <Skeleton variant="circular" width={80} height={80} />
              </Grid>
              <Grid item xs>
                <Skeleton variant="text" width="40%" height={40} />
                <Skeleton variant="text" width="25%" height={24} />
              </Grid>
            </Grid>
          </Card>
        </MDBox>

        {/* Content Skeleton */}
        <MDBox p={2}>
          <Grid container spacing={3}>
            {/* Left Column Skeleton */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <MDBox p={2}>
                  <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 1 }} />
                  <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
                </MDBox>
              </Card>
            </Grid>
            {/* Right Column Skeleton */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <MDBox p={2}>
                  <Skeleton variant="text" width="50%" height={32} sx={{ mb: 2 }} />
                  {[1, 2, 3, 4, 5].map((i) => (
                    <MDBox key={i} mb={2}>
                      <Skeleton variant="text" width="30%" height={20} />
                      <Skeleton variant="text" width="60%" height={20} />
                    </MDBox>
                  ))}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error || !profileData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Error Loading Customer Profile</AlertTitle>
            {error || "Customer not found"}
          </Alert>
          <MDBox display="flex" gap={2}>
            <MDButton
              variant="outlined"
              color="info"
              onClick={handleRetry}
              startIcon={<Icon>refresh</Icon>}
            >
              Try Again
            </MDButton>
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={() => navigate("/pelanggan/maklumat-pelanggan")}
              startIcon={<Icon>arrow_back</Icon>}
            >
              Kembali ke Maklumat Pelanggan
            </MDButton>
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  // Handle edit mode toggle
  const handleEditClick = () => {
    setIsEditMode(true);
    setEditFormData({
      nama: profileData.nama || "",
      no_kp: profileData.no_kp || "",
      no_tel: profileData.no_tel || "",
      emel: profileData.emel || "",
      alamat1_pelanggan: profileData.alamat1_pelanggan || "",
      alamat2_pelanggan: profileData.alamat2_pelanggan || "",
      poskod_pelanggan: profileData.poskod_pelanggan || "",
      bandar_pelanggan: profileData.bandar_pelanggan || "",
      negeri_pelanggan: profileData.negeri_pelanggan || "",
      zon: profileData.zon || "",
      // Company fields
      nama_syarikat: profileData.nama_syarikat || "",
      no_ssm: profileData.no_ssm || "",
      // Next of Kin fields
      nama_waris: profileData.nama_waris || "",
      telefon_waris: profileData.telefon_waris || "",
      hubungan_waris: profileData.hubungan_waris || "",
      alamat_waris: profileData.alamat_waris || "",
      // Bank fields
      no_akaun_bank: profileData.no_akaun_bank || "",
      no_akaun_bank_syarikat: profileData.no_akaun_bank_syarikat || "",
    });
    setSaveError(null);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditFormData({});
    setSaveError(null);
  };

  const handleInputChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const errors = [];
    
    if (!editFormData.nama || editFormData.nama.trim() === "") {
      errors.push("Nama Penuh is required");
    }
    
    if (editFormData.emel && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editFormData.emel)) {
      errors.push("Email format is invalid");
    }
    
    if (editFormData.no_tel && !/^[0-9-+()\s]+$/.test(editFormData.no_tel)) {
      errors.push("Phone number format is invalid");
    }
    
    return errors;
  };

  const handleSaveEdit = async () => {
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setSaveError(validationErrors.join(". "));
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);

      const response = await fetchWithCsrfRetry(`/api/nama/${idNama}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-User-Department": getCurrentUserDepartment(),
        },
        body: JSON.stringify(editFormData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to update customer information");
      }

      const result = await response.json();
      
      // Update local state with new data
      setProfileData((prev) => ({ ...prev, ...editFormData }));
      setIsEditMode(false);
      setSnackbar({ open: true, message: "Customer information updated successfully", color: "success" });
    } catch (err) {
      console.error("Error saving customer data:", err);
      setSaveError(err.message || "Failed to save changes");
      setSnackbar({ open: true, message: err.message || "Failed to save changes", color: "error" });
    } finally {
      setSaving(false);
    }
  };

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // Handle kod harta click with support for new tab (Ctrl/Cmd+Click or middle click)
  const handleKodHartaClick = (kodHarta, noAkaun, event) => {
    const shouldOpenInNewTab = event.ctrlKey || event.metaKey || event.button === 1;
    if (shouldOpenInNewTab) {
      const newTabUrl = `${window.location.origin}/harta/${kodHarta}/${noAkaun}`;
      window.open(newTabUrl, "_blank");
    } else {
      setNavigating(true);
      // Pass customer context via location state
      navigate(`/harta/${kodHarta}/${noAkaun}`, {
        state: { 
          customerName: profileData?.nama,
          customerId: profileData?.id_nama,
          fromProfile: true
        }
      });
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />

      {/* Header */}
      <MDBox position="relative" mb={2}>
        <MDBox
          display="flex"
          alignItems="center"
          position="relative"
          minHeight="6rem"
          borderRadius="xl"
          sx={{
            background: "linear-gradient(90deg,rgb(179, 0, 87) 0%,rgb(248, 81, 167) 100%)",
            backgroundSize: "cover",
            backgroundPosition: "50%",
            overflow: "hidden",
          }}
        />
        <Card
          sx={{
            position: "relative",
            mt: -8,
            mx: 3,
            py: 2,
            px: 2,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <MDBox height="5rem" width="5rem" borderRadius="50%" shadow="xl">
                {/* Placeholder for profile image */}
                <MDBox
                  component="div"
                  height="100%"
                  width="100%"
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ backgroundColor: "info.main" }}
                >
                  <Icon fontSize="large" sx={{ color: "white" }}>
                    person
                  </Icon>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item>
              <MDBox height="100%" mt={0.5} lineHeight={1}>
                <MDTypography variant="h5" fontWeight="medium">
                  {profileData.nama || "N/A"}
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  {profileData.no_kp || "N/A"}
                </MDTypography>
                {userDepartment && (
                  <MDTypography variant="caption" color="info" fontWeight="medium" display="block">
                    Bahagian: {userDepartment}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }}>
              <MDBox
                display="flex"
                justifyContent={{ md: "flex-end" }}
                alignItems="center"
                lineHeight={1}
                flexDirection={{ xs: "column", sm: "row" }}
                gap={1}
              >
                <MDBox display="flex" flexDirection="column" gap={0.5}>
                  <MDTypography
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate("/pelanggan/maklumat-pelanggan")}
                  >
                    ← Kembali ke Maklumat Pelanggan
                  </MDTypography>
                  {/* Breadcrumb */}
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <MDTypography
                      variant="caption"
                      color="text"
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate("/pelanggan/maklumat-pelanggan")}
                    >
                      Maklumat Pelanggan
                    </MDTypography>
                    <MDTypography variant="caption" color="text">›</MDTypography>
                    <MDTypography variant="caption" color="dark" fontWeight="medium">
                      {profileData.nama}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </Card>
      </MDBox>

      {/* Content - Direct display without tabs */}
      <MDBox p={2}>
        <Grid container spacing={3}>
          {/* Left: Account Summary & Harta */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={2}>
                {/* Account Summary */}
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Ringkasan Akaun
                </MDTypography>
                <MDBox mb={2} p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <MDTypography variant="button" fontWeight="bold" color="text">
                      Jumlah Akaun:
                    </MDTypography>
                    <MDTypography variant="h5" fontWeight="bold" color="info">
                      {profileData.total_akaun || 0}
                    </MDTypography>
                  </MDBox>
                  <MDBox mb={1}>
                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
                      No Akaun:
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {profileData.no_akaun_list || "-"}
                    </MDTypography>
                  </MDBox>
                  <MDBox>
                    <MDTypography variant="caption" fontWeight="bold" color="text" display="block">
                      Kod Pelanggan:
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {profileData.kod_pelanggan_list || "-"}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                {/* Harta Table */}
                <Divider sx={{ my: 2 }} />
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Senarai Harta
                </MDTypography>
                <DataTable
                  table={{
                    columns: [
                      { Header: "kod harta", accessor: "kodHarta", width: "25%", align: "left" },
                      { Header: "no akaun", accessor: "noAkaun", width: "35%", align: "left" },
                      { Header: "kadar sewa", accessor: "kadarSewa", width: "25%", align: "right" },
                      { Header: "status", accessor: "status", width: "15%", align: "center" },
                    ],
                    rows:
                      profileData.akaun_list && profileData.akaun_list.length > 0
                        ? profileData.akaun_list.map((akaun) => {
                            const kodHarta = akaun.kod_harta;
                            const noAkaun = akaun.no_akaun || "N/A";
                            const status = akaun.status_akaun || "N/A";
                            const kadarSewa = akaun.kadar_sewa
                              ? `RM ${parseFloat(akaun.kadar_sewa).toFixed(2)}`
                              : "-";

                            return {
                              kodHarta: (
                                <MDBox display="flex" alignItems="center" gap={0.5}>
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    color="info"
                                    sx={{
                                      cursor: navigating ? "wait" : "pointer",
                                      textDecoration: "underline",
                                      textTransform: "uppercase",
                                      opacity: navigating ? 0.6 : 1,
                                    }}
                                    onClick={(e) => !navigating && handleKodHartaClick(kodHarta, noAkaun, e)}
                                    onMouseDown={(e) => {
                                      if (e.button === 1 && !navigating) {
                                        e.preventDefault();
                                        handleKodHartaClick(kodHarta, noAkaun, e);
                                      }
                                    }}
                                  >
                                    {kodHarta || "N/A"}
                                  </MDTypography>
                                  {navigating && <CircularProgress size={10} />}
                                </MDBox>
                              ),
                              noAkaun: (
                                <MDTypography variant="caption" color="text">
                                  {noAkaun}
                                </MDTypography>
                              ),
                              kadarSewa: (
                                <MDTypography variant="caption" color="text">
                                  {kadarSewa}
                                </MDTypography>
                              ),
                              status: (
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  sx={{
                                    color:
                                      status === "Aktif"
                                        ? "green"
                                        : status === "Tidak Aktif"
                                        ? "red"
                                        : undefined,
                                    textTransform: "capitalize",
                                  }}
                                >
                                  {status}
                                </MDTypography>
                              ),
                            };
                          })
                        : [],
                  }}
                  showTotalEntries={false}
                  isSorted={false}
                  noEndBorder
                  entriesPerPage={false}
                  canSearch={false}
                />

                {/* Dokumen Sokongan Section */}
                <Divider sx={{ my: 3 }} />
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Dokumen Sokongan
                  </MDTypography>
                  <MDButton
                    variant="outlined"
                    color="success"
                    size="small"
                    startIcon={<Icon>upload</Icon>}
                  >
                    Upload
                  </MDButton>
                </MDBox>
                
                <DataTable
                  table={{
                    columns: [
                      { Header: "bil", accessor: "bil", width: "10%", align: "center" },
                      { Header: "nama dokumen", accessor: "namaDokumen", width: "60%", align: "left" },
                      { Header: "tindakan", accessor: "tindakan", width: "30%", align: "center" },
                    ],
                    rows: [
                      {
                        bil: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            1
                          </MDTypography>
                        ),
                        namaDokumen: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            Surat Perjanjian Sewa 2024
                          </MDTypography>
                        ),
                        tindakan: (
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            iconOnly
                            sx={{ minWidth: 32, width: 32, height: 32 }}
                          >
                            <Icon fontSize="small">print</Icon>
                          </MDButton>
                        ),
                      },
                      {
                        bil: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            2
                          </MDTypography>
                        ),
                        namaDokumen: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            Borang Permohonan Sewa
                          </MDTypography>
                        ),
                        tindakan: (
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            iconOnly
                            sx={{ minWidth: 32, width: 32, height: 32 }}
                          >
                            <Icon fontSize="small">print</Icon>
                          </MDButton>
                        ),
                      },
                      {
                        bil: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            3
                          </MDTypography>
                        ),
                        namaDokumen: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            Salinan IC Penyewa
                          </MDTypography>
                        ),
                        tindakan: (
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            iconOnly
                            sx={{ minWidth: 32, width: 32, height: 32 }}
                          >
                            <Icon fontSize="small">print</Icon>
                          </MDButton>
                        ),
                      },
                      {
                        bil: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            4
                          </MDTypography>
                        ),
                        namaDokumen: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            Resit Bayaran Deposit
                          </MDTypography>
                        ),
                        tindakan: (
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            iconOnly
                            sx={{ minWidth: 32, width: 32, height: 32 }}
                          >
                            <Icon fontSize="small">print</Icon>
                          </MDButton>
                        ),
                      },
                      {
                        bil: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            5
                          </MDTypography>
                        ),
                        namaDokumen: (
                          <MDTypography variant="caption" color="text" fontWeight="medium">
                            Surat Kebenaran Pihak Berkuasa
                          </MDTypography>
                        ),
                        tindakan: (
                          <MDButton
                            variant="outlined"
                            color="info"
                            size="small"
                            iconOnly
                            sx={{ minWidth: 32, width: 32, height: 32 }}
                          >
                            <Icon fontSize="small">print</Icon>
                          </MDButton>
                        ),
                      },
                    ],
                  }}
                  showTotalEntries={false}
                  isSorted={false}
                  noEndBorder
                  entriesPerPage={false}
                  canSearch={false}
                />
              </MDBox>
            </Card>
          </Grid>
          {/* Right: Customer Info */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <MDBox p={2}>
                {/* Personal Information */}
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Maklumat Peribadi
                  </MDTypography>
                  {!isEditMode ? (
                    <MDButton
                      variant="outlined"
                      color="info"
                      size="small"
                      startIcon={<Icon>edit</Icon>}
                      onClick={handleEditClick}
                    >
                      Edit
                    </MDButton>
                  ) : (
                    <MDBox display="flex" gap={1}>
                      <MDButton
                        variant="outlined"
                        color="success"
                        size="small"
                        startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <Icon>save</Icon>}
                        onClick={handleSaveEdit}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save"}
                      </MDButton>
                      <MDButton
                        variant="outlined"
                        color="secondary"
                        size="small"
                        startIcon={<Icon>close</Icon>}
                        onClick={handleCancelEdit}
                        disabled={saving}
                      >
                        Cancel
                      </MDButton>
                    </MDBox>
                  )}
                </MDBox>

                {saveError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>
                    {saveError}
                  </Alert>
                )}

                <MDBox mb={2} p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    Nama Penuh: {isEditMode && <span style={{ color: "red" }}>*</span>}
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" mb={1} display="block">
                      {profileData.nama || "N/A"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      value={editFormData.nama || ""}
                      onChange={(e) => handleInputChange("nama", e.target.value)}
                      size="small"
                      required
                      sx={{ mb: 1 }}
                    />
                  )}
                  
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    No KP:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" mb={1} display="block">
                      {profileData.no_kp || "N/A"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      value={editFormData.no_kp || ""}
                      onChange={(e) => handleInputChange("no_kp", e.target.value)}
                      size="small"
                      sx={{ mb: 1 }}
                    />
                  )}
                  
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    No Tel:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" mb={1} display="block">
                      {profileData.no_tel || "N/A"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      value={editFormData.no_tel || ""}
                      onChange={(e) => handleInputChange("no_tel", e.target.value)}
                      size="small"
                      placeholder="e.g., 012-3456789"
                      sx={{ mb: 1 }}
                    />
                  )}
                  
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    Email:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" mb={1} display="block">
                      {profileData.emel || "N/A"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      type="email"
                      value={editFormData.emel || ""}
                      onChange={(e) => handleInputChange("emel", e.target.value)}
                      size="small"
                      placeholder="example@email.com"
                      sx={{ mb: 1 }}
                    />
                  )}
                  
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    Alamat:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" mb={1} display="block">
                      {[
                        profileData.alamat1_pelanggan,
                        profileData.alamat2_pelanggan,
                        profileData.poskod_pelanggan,
                        profileData.bandar_pelanggan,
                        profileData.negeri_pelanggan,
                      ]
                        .filter(Boolean)
                        .join(", ") || "N/A"}
                    </MDTypography>
                  ) : (
                    <MDBox>
                      <MDInput
                        fullWidth
                        value={editFormData.alamat1_pelanggan || ""}
                        onChange={(e) => handleInputChange("alamat1_pelanggan", e.target.value)}
                        size="small"
                        placeholder="Alamat 1"
                        sx={{ mb: 1 }}
                      />
                      <MDInput
                        fullWidth
                        value={editFormData.alamat2_pelanggan || ""}
                        onChange={(e) => handleInputChange("alamat2_pelanggan", e.target.value)}
                        size="small"
                        placeholder="Alamat 2"
                        sx={{ mb: 1 }}
                      />
                      <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={4}>
                          <MDInput
                            fullWidth
                            value={editFormData.poskod_pelanggan || ""}
                            onChange={(e) => handleInputChange("poskod_pelanggan", e.target.value)}
                            size="small"
                            placeholder="Poskod"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <MDInput
                            fullWidth
                            value={editFormData.bandar_pelanggan || ""}
                            onChange={(e) => handleInputChange("bandar_pelanggan", e.target.value)}
                            size="small"
                            placeholder="Bandar"
                          />
                        </Grid>
                      </Grid>
                      <MDInput
                        fullWidth
                        value={editFormData.negeri_pelanggan || ""}
                        onChange={(e) => handleInputChange("negeri_pelanggan", e.target.value)}
                        size="small"
                        placeholder="Negeri"
                        sx={{ mb: 1 }}
                      />
                    </MDBox>
                  )}
                  
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    Zon:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" display="block">
                      {profileData.zon || "-"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      value={editFormData.zon || ""}
                      onChange={(e) => handleInputChange("zon", e.target.value)}
                      size="small"
                    />
                  )}
                </MDBox>

                {/* Company Information */}
                {(profileData.nama_syarikat || isEditMode) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="h6" fontWeight="medium" mb={2}>
                      Maklumat Syarikat
                    </MDTypography>
                    <MDBox mb={2} p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                      <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        Nama Syarikat:
                      </MDTypography>
                      {!isEditMode ? (
                        <MDTypography variant="caption" color="text" mb={1} display="block">
                          {profileData.nama_syarikat || "-"}
                        </MDTypography>
                      ) : (
                        <MDInput
                          fullWidth
                          value={editFormData.nama_syarikat || ""}
                          onChange={(e) => handleInputChange("nama_syarikat", e.target.value)}
                          size="small"
                          placeholder="Nama Syarikat"
                          sx={{ mb: 1 }}
                        />
                      )}
                      
                      <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        No SSM:
                      </MDTypography>
                      {!isEditMode ? (
                        <MDTypography variant="caption" color="text" display="block">
                          {profileData.no_ssm || "-"}
                        </MDTypography>
                      ) : (
                        <MDInput
                          fullWidth
                          value={editFormData.no_ssm || ""}
                          onChange={(e) => handleInputChange("no_ssm", e.target.value)}
                          size="small"
                          placeholder="No SSM"
                        />
                      )}
                    </MDBox>
                  </>
                )}

                {/* Waris Information */}
                {(profileData.nama_waris || isEditMode) && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <MDTypography variant="h6" fontWeight="medium" mb={2}>
                      Maklumat Waris
                    </MDTypography>
                    <MDBox mb={2} p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                      <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        Nama Waris:
                      </MDTypography>
                      {!isEditMode ? (
                        <MDTypography variant="caption" color="text" mb={1} display="block">
                          {profileData.nama_waris || "-"}
                        </MDTypography>
                      ) : (
                        <MDInput
                          fullWidth
                          value={editFormData.nama_waris || ""}
                          onChange={(e) => handleInputChange("nama_waris", e.target.value)}
                          size="small"
                          placeholder="Nama Waris"
                          sx={{ mb: 1 }}
                        />
                      )}
                      
                      <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        No Tel Waris:
                      </MDTypography>
                      {!isEditMode ? (
                        <MDTypography variant="caption" color="text" mb={1} display="block">
                          {profileData.telefon_waris || "-"}
                        </MDTypography>
                      ) : (
                        <MDInput
                          fullWidth
                          value={editFormData.telefon_waris || ""}
                          onChange={(e) => handleInputChange("telefon_waris", e.target.value)}
                          size="small"
                          placeholder="e.g., 012-3456789"
                          sx={{ mb: 1 }}
                        />
                      )}
                      
                      <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        Hubungan:
                      </MDTypography>
                      {!isEditMode ? (
                        <MDTypography variant="caption" color="text" mb={1} display="block">
                          {profileData.hubungan_waris || "-"}
                        </MDTypography>
                      ) : (
                        <MDInput
                          fullWidth
                          value={editFormData.hubungan_waris || ""}
                          onChange={(e) => handleInputChange("hubungan_waris", e.target.value)}
                          size="small"
                          placeholder="e.g., Anak, Isteri, Suami"
                          sx={{ mb: 1 }}
                        />
                      )}
                      
                      <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                        Alamat Waris:
                      </MDTypography>
                      {!isEditMode ? (
                        <MDTypography variant="caption" color="text" display="block">
                          {profileData.alamat_waris || "-"}
                        </MDTypography>
                      ) : (
                        <MDInput
                          fullWidth
                          multiline
                          rows={2}
                          value={editFormData.alamat_waris || ""}
                          onChange={(e) => handleInputChange("alamat_waris", e.target.value)}
                          size="small"
                          placeholder="Alamat Waris"
                        />
                      )}
                    </MDBox>
                  </>
                )}

                {/* Bank Account Information */}
                <Divider sx={{ my: 2 }} />
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Maklumat Bank
                </MDTypography>
                <MDBox p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    No Akaun Bank:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" mb={1} display="block">
                      {profileData.no_akaun_bank || "-"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      value={editFormData.no_akaun_bank || ""}
                      onChange={(e) => handleInputChange("no_akaun_bank", e.target.value)}
                      size="small"
                      placeholder="No Akaun Bank Peribadi"
                      sx={{ mb: 1 }}
                    />
                  )}
                  
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    No Akaun Bank Syarikat:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" mb={1} display="block">
                      {profileData.no_akaun_bank_syarikat || "-"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      value={editFormData.no_akaun_bank_syarikat || ""}
                      onChange={(e) => handleInputChange("no_akaun_bank_syarikat", e.target.value)}
                      size="small"
                      placeholder="No Akaun Bank Syarikat"
                      sx={{ mb: 1 }}
                    />
                  )}
                  
                  <MDTypography variant="caption" fontWeight="bold" color="text" display="block" mb={0.5}>
                    Nama Bank:
                  </MDTypography>
                  {!isEditMode ? (
                    <MDTypography variant="caption" color="text" display="block">
                      {profileData.nama_bank || "-"}
                    </MDTypography>
                  ) : (
                    <MDInput
                      fullWidth
                      value={editFormData.nama_bank || ""}
                      onChange={(e) => handleInputChange("nama_bank", e.target.value)}
                      size="small"
                      placeholder="Contoh: Maybank, CIMB, RHB"
                    />
                  )}
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Success/Error Snackbar */}
      <MDSnackbar
        color={snackbar.color}
        icon={snackbar.color === "success" ? "check" : "warning"}
        title={snackbar.color === "success" ? "Success" : "Error"}
        content={snackbar.message}
        open={snackbar.open}
        onClose={closeSnackbar}
        close={closeSnackbar}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default CustomerProfile;
